import { ChevronDown, Edit3, LogOut, Settings, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ROUTES } from '@/shared/config/routes';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui';
import { useAuth, type AuthUser } from '@/features/auth';
import { RoleBadge } from '@/entities/user';

/** Authenticated account dropdown: profile, role-gated actions, sign out. */
export function UserMenu({ user }: { user: AuthUser }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const isStaff = user.role === 'admin' || user.role === 'writer';

  const handleSignOut = async () => {
    try {
      await signOut();
      void navigate(ROUTES.home);
    } catch {
      toast.error('Could not sign out. Please try again.');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-full border border-border py-1 pl-1 pr-3 transition-all hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 dark:hover:bg-zinc-800">
        <Avatar
          initials={user.initials}
          color={user.avatarColor}
          src={user.avatarUrl}
          size="sm"
        />
        <span className="hidden text-sm font-medium text-foreground sm:block">
          {user.name.split(' ')[0]}
        </span>
        <ChevronDown className="h-3 w-3 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>
          <p className="text-sm font-semibold text-foreground">{user.name}</p>
          <RoleBadge role={user.role} className="mt-1" />
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate(ROUTES.profile)}>
          <UserCircle className="h-4 w-4 text-muted-foreground" />
          Manage profile
        </DropdownMenuItem>
        {isStaff ? (
          <DropdownMenuItem onSelect={() => navigate(ROUTES.write)}>
            <Edit3 className="h-4 w-4 text-muted-foreground" />
            Write article
          </DropdownMenuItem>
        ) : null}
        {user.role === 'admin' ? (
          <DropdownMenuItem onSelect={() => navigate(ROUTES.admin)}>
            <Settings className="h-4 w-4 text-muted-foreground" />
            Admin panel
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => void handleSignOut()}>
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
